// ui/src/components/calendar/CalendarDayCell.tsx
import React from 'react';
import type { CalendarDay, CalendarDayAction } from './types';

interface CalendarDayCellProps {
  day: CalendarDay;
  /** Whether this date falls in the currently displayed month (month mode only). Trailing/leading pad cells from adjacent months render muted. */
  inCurrentPeriod: boolean;
  isToday: boolean;
  /** Selected for the parent calendar's absence range — renders a highlight ring. */
  isHighlighted?: boolean;
  onClick: (date: string) => void;
  /** The host's day-click action wording, or null for a read-only host — see
   *  CalendarGridProps.dayAction. */
  action: (isClosed: boolean) => CalendarDayAction | null;
}

const CalendarDayCell: React.FC<CalendarDayCellProps> = ({
  day,
  inCurrentPeriod,
  isToday,
  isHighlighted = false,
  onClick,
  action,
}) => {
  // A closure can cover a day with zero scheduled classes (e.g. a holiday
  // landing on a weekend) — day.closureLabel is the location-level signal
  // for that case; a suppressed class entry is the fallback for callers that
  // don't fetch closures separately. Either one means the day is closed.
  const closedEntry = day.classes.find((entry) => entry.suppressedBy === 'CLOSURE');
  const isClosed = Boolean(closedEntry) || Boolean(day.closureLabel);
  const closedLabel = day.closureLabel ?? closedEntry?.closureLabel ?? 'Closed';
  const dayNumber = Number(day.date.slice(-2));
  const dayAction = action(isClosed);

  return (
    <button
      type="button"
      data-testid={`calendar-day-${day.date}`}
      onClick={() => onClick(day.date)}
      // The whole cell is the click target. aria-label + cursor-pointer + the
      // hover cue below make that discoverable — worded by the HOST, because
      // what a click does differs per role (admin: manage closure; parent:
      // view day / pick absence dates; staff: nothing).
      aria-label={[day.date, isClosed ? 'closed' : null, dayAction?.label]
        .filter(Boolean)
        .join(' — ')}
      className={[
        'group relative flex min-h-[6rem] w-full flex-col items-stretch gap-1 rounded-md border p-2 text-left align-top transition-colors',
        dayAction ? 'cursor-pointer' : 'cursor-default',
        isHighlighted
          ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-400'
          : isClosed
          ? 'border-slate-300 bg-slate-100'
          : 'border-gray-200 bg-white hover:bg-gray-50',
        !inCurrentPeriod ? 'opacity-50' : '',
        isToday && !isHighlighted ? 'ring-2 ring-primary-500' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        className={['text-sm font-medium', isClosed ? 'text-slate-600' : 'text-gray-900'].join(' ')}
      >
        {dayNumber}
      </span>

      {/* Desktop hover cue: names the hidden action so the cell reads as
          interactive. Hidden on mobile (no hover) — the tip above the grid and
          the tap target cover that case. Non-current-month pad cells and
          read-only hosts (null action) skip it. */}
      {inCurrentPeriod && dayAction && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-1 top-1 hidden rounded bg-slate-700/90 px-1.5 py-0.5 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 lg:inline-flex"
        >
          {dayAction.hoverCue}
        </span>
      )}

      {isClosed && (
        <span
          data-testid={`calendar-day-closed-${day.date}`}
          className="inline-flex w-fit items-center rounded bg-slate-200 px-1.5 py-0.5 text-xs font-medium text-slate-700"
        >
          {closedLabel}
        </span>
      )}

      {!isClosed &&
        day.classes.map((entry) => (
          <span
            key={entry.classId}
            className={[
              'truncate rounded px-1.5 py-0.5 text-xs font-medium',
              entry.suppressedBy === 'CANCELLED'
                ? 'bg-amber-100 text-amber-800 line-through'
                : 'bg-primary-50 text-primary-700',
            ].join(' ')}
            title={entry.overrideReason ?? entry.className}
          >
            {entry.className}
          </span>
        ))}
    </button>
  );
};

export default CalendarDayCell;
